import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { OciService } from '../../services/oci.service';
import { ProjectfileService } from '../../services/projectfile.service';
import { ProjectFileModel } from '../../models/projectfile.model';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-file-viewer',
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule, Toast],
  templateUrl: './file-viewer.component.html',
  styleUrls: ['./file-viewer.component.css']
})
export class FileViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('viewer', { static: false }) viewerRef!: ElementRef;

  // Component properties
  projectId: number = 0;
  currentFileId: number = 0;
  currentFile: ProjectFileModel | null = null;

  // WebViewer instance
  webViewerInstance: WebViewerInstance | null = null;
  isWebViewerReady: boolean = false;
  isLoading: boolean = true;
  private annotationSaveTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private annotationDirty = false;
  private isLoadingServerAnnotations = false;
  private currentAnnotationXfdf = '';
  private activeDocumentObjectUrl: string | null = null;
  private readonly annotationSaveDelayMs = 1000;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private ociService: OciService,
    private projectfileService: ProjectfileService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Extract route parameters
    this.route.paramMap.subscribe(params => {
      const projectIdParam = params.get('projectId');
      const fileIdParam = params.get('fileId');

      if (projectIdParam) {
        this.projectId = parseInt(projectIdParam);
      }

      if (fileIdParam) {
        this.currentFileId = parseInt(fileIdParam);
      }

      console.log('File Viewer initialized with:', {
        projectId: this.projectId,
        fileId: this.currentFileId
      });
    });
  }

  ngAfterViewInit(): void {
    // Initialize WebViewer after view is ready
    this.initializeAndLoad();
  }

  /**
   * Initialize WebViewer and load file list
   */
  private async initializeAndLoad(): Promise<void> {
    try {
      this.isLoading = true;
      await this.initializeWebViewer();
      await this.loadFileList();
    } catch (error) {
      console.error('Error initializing viewer:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to initialize viewer'
      });
      this.isLoading = false;
    }
  }

  /**
   * Initialize WebViewer instance
   */
  private async initializeWebViewer(): Promise<void> {
    if (!this.viewerRef?.nativeElement) {
      console.error('Viewer container not found');
      return;
    }

    try {
      console.log('Initializing WebViewer...');

      this.webViewerInstance = await WebViewer.Iframe(
        {
          path: 'assets/webviewer/lib',
          licenseKey: environment.apryseWebLicenseKey,
          enableAnnotations: true,
          enableMeasurement: true,
          enableRedaction: false,
        },
        this.viewerRef.nativeElement
      );

      console.log('WebViewer instance created');

      // Wait for WebViewer UI to be fully ready
      await new Promise<void>((resolve) => setTimeout(() => resolve(), 1000));

      this.isWebViewerReady = true;
      this.attachAnnotationListeners();
      console.log('WebViewer initialized successfully');
    } catch (error) {
      console.error('Error initializing WebViewer:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to initialize document viewer.'
      });
      throw error;
    }
  }

  /**
   * Fetch the current file and load it
   */
  private async loadFileList(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.projectfileService.getProjectFiles(this.projectId).subscribe({
        next: (folders) => {
          // Find the current file in all folders
          let foundFile: ProjectFileModel | null = null;

          for (const folder of folders) {
            if (folder.projectFileList && folder.projectFileList.length > 0) {
              foundFile = folder.projectFileList.find(f => f.fileId === this.currentFileId) || null;
              if (foundFile) break;
            }
          }

          if (foundFile) {
            this.currentFile = foundFile;
            console.log('Found file:', foundFile.fileName || foundFile.fileFullName);
            this.loadDocument(this.currentFile);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'File Not Found',
              detail: 'The requested file could not be found.'
            });
            this.isLoading = false;
          }
          resolve();
        },
        error: (error) => {
          console.error('Error loading file:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load file.'
          });
          this.isLoading = false;
          reject(error);
        }
      });
    });
  }

  /**
   * Load document into WebViewer
   */
  private async loadDocument(file: ProjectFileModel): Promise<void> {
    if (!this.webViewerInstance || !this.isWebViewerReady) {
      console.error('WebViewer not ready');
      return;
    }

    // Check if file type is supported
    const fileName = file.fileName || file.fileFullName || '';
    if (!this.isFileTypeSupported(fileName)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Unsupported File Type',
        detail: `Cannot display ${fileName}. File type is not supported.`
      });
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.annotationDirty = false;
    this.currentAnnotationXfdf = '';

    // Download file from OCI storage
    this.ociService.getOciResourceEnhanced(file.filePath || '').subscribe({
      next: async (blob: Blob) => {
        console.log('File downloaded successfully:', fileName, 'Size:', blob.size);

        await this.loadServerAnnotationXfdf(file);

        this.releaseActiveDocumentObjectUrl();
        // Create blob URL for WebViewer
        const blobUrl = URL.createObjectURL(blob);
        this.activeDocumentObjectUrl = blobUrl;

        // Load into WebViewer
        this.webViewerInstance!.Core.documentViewer.addEventListener(
          'documentLoaded',
          () => {
            void this.importServerAnnotations();
          },
          { once: true } as any
        );

        this.webViewerInstance!.UI.loadDocument(blobUrl, {
          filename: fileName,
          extension: this.getFileExtension(fileName)
        });

        this.isLoading = false;

        console.log('Document loaded into WebViewer:', fileName);
      },
      error: (error) => {
        console.error('Error downloading file:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to load ${fileName}.`
        });
        this.isLoading = false;
      }
    });
  }

  /**
   * Get file extension from filename
   */
  private getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot > -1 ? fileName.substring(lastDot + 1).toLowerCase() : '';
  }

  ngOnDestroy(): void {
    void this.persistAnnotationsNow();
    if (this.annotationSaveTimeoutId) {
      clearTimeout(this.annotationSaveTimeoutId);
      this.annotationSaveTimeoutId = null;
    }
    this.releaseActiveDocumentObjectUrl();

    // Cleanup WebViewer instance
    if (this.webViewerInstance) {
      this.webViewerInstance = null;
    }
  }

  /**
   * Get supported file types for WebViewer
   */
  private getSupportedFileTypes(): string[] {
    return [
      // Native support
      '.pdf',
      '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif',
      // Server required
      '.doc', '.docx',
      '.xls', '.xlsx',
      '.ppt', '.pptx',
      '.dwg', '.dxf'
    ];
  }

  /**
   * Check if file type is supported
   */
  private isFileTypeSupported(fileName: string): boolean {
    const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    return this.getSupportedFileTypes().includes(extension);
  }

  async goBackToFiles(): Promise<void> {
    await this.persistAnnotationsNow();
    this.router.navigate(['/files', this.projectId]);
  }

  private get supportsProjectFileAnnotations(): boolean {
    const fileName = this.currentFile?.fileName || this.currentFile?.fileFullName || this.currentFile?.filePath || '';
    return this.getFileExtension(fileName) === 'pdf';
  }

  private attachAnnotationListeners(): void {
    if (!this.webViewerInstance) {
      return;
    }

    const { annotationManager } = this.webViewerInstance.Core;
    annotationManager.addEventListener('annotationChanged', (_annotations: any[], _action: string, info: any) => {
      if (!this.supportsProjectFileAnnotations || this.isLoadingServerAnnotations) {
        return;
      }

      if (info?.imported) {
        return;
      }

      this.annotationDirty = true;
      this.scheduleAnnotationSave();
    });
  }

  private async loadServerAnnotationXfdf(file: ProjectFileModel): Promise<void> {
    this.currentAnnotationXfdf = '';
    if (!this.supportsProjectFileAnnotations) {
      return;
    }

    try {
      const annotation = await firstValueFrom(this.projectfileService.getProjectFileAnnotation(file.fileId));
      this.currentAnnotationXfdf = annotation.annotationXfdf || '';
    } catch (error) {
      console.warn('Unable to load project file annotation:', error);
    }
  }

  private async importServerAnnotations(): Promise<void> {
    if (!this.webViewerInstance || !this.currentAnnotationXfdf) {
      return;
    }

    this.isLoadingServerAnnotations = true;
    try {
      await this.webViewerInstance.Core.annotationManager.importAnnotations(this.currentAnnotationXfdf);
      this.annotationDirty = false;
    } finally {
      this.isLoadingServerAnnotations = false;
    }
  }

  private scheduleAnnotationSave(): void {
    if (this.annotationSaveTimeoutId) {
      clearTimeout(this.annotationSaveTimeoutId);
    }

    this.annotationSaveTimeoutId = setTimeout(() => {
      void this.persistAnnotationsNow();
    }, this.annotationSaveDelayMs);
  }

  private async persistAnnotationsNow(): Promise<void> {
    if (!this.webViewerInstance || !this.currentFile || !this.supportsProjectFileAnnotations || !this.annotationDirty) {
      return;
    }

    if (this.annotationSaveTimeoutId) {
      clearTimeout(this.annotationSaveTimeoutId);
      this.annotationSaveTimeoutId = null;
    }

    try {
      const { annotationManager } = this.webViewerInstance.Core;
      const annotations = annotationManager.getAnnotationsList();
      const annotationXfdf = annotations.length > 0
        ? await annotationManager.exportAnnotations({
            generateInlineAppearances: true,
            links: false,
            widgets: false
          })
        : null;

      await firstValueFrom(this.projectfileService.updateProjectFileAnnotation(this.currentFile.fileId, annotationXfdf));
      this.annotationDirty = false;
    } catch (error) {
      console.error('Error saving project file annotation:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Annotation not saved',
        detail: 'Unable to save the PDF annotation. Please try again.'
      });
    }
  }

  private releaseActiveDocumentObjectUrl(): void {
    if (this.activeDocumentObjectUrl) {
      URL.revokeObjectURL(this.activeDocumentObjectUrl);
      this.activeDocumentObjectUrl = null;
    }
  }
}
